package dev.kuml.portal.web.auth

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.components.languageSwitcher
import dev.kuml.portal.web.components.legalLinks
import dev.kuml.portal.web.i18n.t
import io.kvision.core.Container
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.image
import kotlinx.coroutines.launch

/**
 * R-3: the landing page a clicked `{baseUrl}/verify-email/{token}` link from
 * `dev.kuml.portal.server.email.EmailService.sendEmailVerification` actually resolves to via
 * [dev.kuml.portal.web.routing.Router]'s `/verify-email/:token` route -- the ONLY caller of
 * [PortalApi.verifyEmail]. Unauthenticated, same shell/footer as [authScreen]: a verification link
 * is followed straight from an email client, often with no session at all.
 *
 * The `POST /api/auth/verify-email` fires exactly once, on mount, with no retry button --
 * `dev.kuml.portal.server.users.EmailVerificationService.consume` is single-use server-side (see
 * that method's KDoc: a redeemed or expired token is indistinguishable from an unknown one), so
 * resubmitting the SAME token from this same screen can never turn a failure into a success. The
 * only recovery path on failure is logging in and requesting a fresh link via
 * [PortalApi.resendVerification] (surfaced by
 * [dev.kuml.portal.web.components.emailVerificationBanner] once logged in).
 */
public fun Container.verifyEmailScreen(
    api: PortalApi,
    token: String,
    onImpressumClick: () -> Unit,
    onDatenschutzClick: () -> Unit,
    onAgbClick: () -> Unit,
    onContinueToLogin: () -> Unit,
) {
    div(className = "kportal-auth-shell") {
        div(className = "kportal-auth-card") {
            div(className = "kportal-wordmark") {
                image(src = "img/logo.svg", alt = "kUML").addCssClass("kportal-logo-light")
                image(src = "img/logo-negative.svg", alt = "kUML").addCssClass("kportal-logo-dark")
            }

            val titleDiv = div(className = "kportal-auth-title")
            val messageDiv = div(className = "kportal-auth-message") { setAttribute("aria-live", "polite") }
            val actionsHost = div(className = "kportal-auth-actions")

            titleDiv.content = t.auth.verifyingTitle
            messageDiv.content = t.auth.verifyingMessage

            AppScope.launch {
                when (api.verifyEmail(token)) {
                    is ApiResult.Success -> {
                        titleDiv.content = t.auth.verifySuccessTitle
                        messageDiv.content = t.auth.verifySuccessMessage
                        actionsHost
                            .button(t.auth.continueToLogin, className = "kportal-btn kportal-btn-primary kportal-btn-auto")
                            .onClick { onContinueToLogin() }
                    }
                    // Every non-success outcome (400 `verification_token_invalid`, a network
                    // failure, an unexpected status) collapses into the SAME dead-end copy -- there
                    // is nothing actionable to distinguish for the caller: the token is either
                    // wrong/expired/consumed, or the request never reached the server at all, and
                    // either way the only way forward is a fresh link.
                    else -> {
                        titleDiv.content = t.auth.verifyFailureTitle
                        messageDiv.content = t.auth.verifyFailureMessage
                        actionsHost
                            .button(t.auth.continueToLogin, className = "kportal-btn kportal-btn-secondary kportal-btn-auto")
                            .onClick { onContinueToLogin() }
                    }
                }
            }
        }
        div(className = "kportal-auth-footer") {
            legalLinks(onImpressumClick, onDatenschutzClick, onAgbClick)
            languageSwitcher()
        }
    }
}
